/**
 * 首頁「還沒有 AI 預測」那組 hero 的配色開關：藍色 ↔ 淡藍霧（Skin V2 那套）。
 *
 * 只管冷啟動這一組（ColdStartNewHero、SurveyCycleEstimateHero、SurveyPredictionPendingHero）。
 * 已經有預測的 AI hero 維持藍色漸層並依庫存狀態變色——那頁的主訊息是「該不該叫瓦斯」，
 * 靠底色的急迫度傳達，換成一片淡霧會把它抹平。
 *
 * 做法與瓦斯訂購的 `?skin=v1|v2` 一致：網址參數寫進 sessionStorage，跨導頁保留。
 *
 * 切換一律走 reload：hero 的顏色散在十幾個原生 div 的 inline style 裡，
 * 用 React state 傳下去要改的檔案比整個開關還多；重載最省事也最不會出錯。
 */
export const HOME_SKIN_KEY = "homeSkin";

export type HomeSkin = "blue" | "haze";

const read = (): HomeSkin | null => {
  if (typeof window === "undefined") return null;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("home");
    if (fromUrl === "blue" || fromUrl === "haze") sessionStorage.setItem(HOME_SKIN_KEY, fromUrl);
    const saved = sessionStorage.getItem(HOME_SKIN_KEY);
    return saved === "blue" || saved === "haze" ? saved : null;
  } catch {
    return null; // 私密模式等讀寫失敗 → 當作沒設定
  }
};

/** 冷啟動 hero 是否為淡藍霧版。預設 true；`?home=blue` 或帳戶選單裡的切換可退回舊的藍色。 */
export const isHomeHaze = (): boolean => read() !== "blue";

export const setHomeSkin = (skin: HomeSkin): void => {
  try {
    sessionStorage.setItem(HOME_SKIN_KEY, skin);
  } catch {
    // 忽略：寫不進去就只影響這一次切換
  }
  window.location.reload();
};

/**
 * 淡霧 hero 的主文字色。
 * 這組 hero 還沒有庫存狀態可依（預測都還沒生出來），所以只有一種底、一種墨色，
 * 不像 AI hero 要分四種狀態。
 */
export const HAZE_HERO_INK = "#0f4c81";

/**
 * 首頁三個 hero 的大數字字體（問卷週期初估、漸層 hero 的平均天數、AI 預測的區間）。
 *
 * 內文是粉圓，但粉圓只有 400 一個字重——96px 下的 800 是瀏覽器合成的假粗體，
 * 筆畫邊緣會毛，而這是整頁最大的元素。
 *
 * Fredoka 600 是幾何圓體：圓頭筆畫和吉祥物、膠囊鈕同語彙，字腔又比粉圓緊得多，
 * 放大時明顯是「另一套字」而不是粉圓變粗。
 * 次選是 Outfit 800（末端切平、更中性現代，對比更強但和圓潤的吉祥物較疏遠）——
 * 要換的話改這一行，並把 public/index.html 的 Google Fonts 參數一起換掉。
 *
 * 沒有中文字符，所以只掛在數字上；「天」與其餘中文仍然吃粉圓（var(--font-sans)）。
 */
export const DAY_NUMBER_FONT = "'Fredoka', var(--font-sans)";
